import { isFeatureFlagEnabled } from '@/modules/featureFlags/isFeatureFlagEnabled';

import {
  FEATURE_ANNOUNCEMENTS,
  FeatureAnnouncementConfig,
  FeatureAnnouncementId,
} from './featureAnnouncements.config';

/**
 * Gets all announcements that should be shown based on:
 * - Current date is within start/end date range
 * - Feature flag is enabled (if specified)
 */
export function getActiveAnnouncements(): FeatureAnnouncementConfig[] {
  const now = new Date();

  return FEATURE_ANNOUNCEMENTS.filter((announcement) => {
    // Check date range
    const isInDateRange =
      announcement.startDate <= now && announcement.endDate >= now;
    if (!isInDateRange) {
      return false;
    }

    // Check feature flag if specified
    if (announcement.featureFlagName) {
      return isFeatureFlagEnabled(announcement.featureFlagName);
    }

    return true;
  });
}

/**
 * Checks if a specific announcement has been viewed by the user
 */
export function isAnnouncementViewed(
  announcementId: FeatureAnnouncementId,
  viewedIds: string[]
): boolean {
  return viewedIds.includes(announcementId);
}

/**
 * Gets announcements that haven't been viewed yet
 */
export function getUnviewedAnnouncements(
  viewedIds: string[]
): FeatureAnnouncementConfig[] {
  const activeAnnouncements = getActiveAnnouncements();

  return activeAnnouncements.filter(
    (announcement) => !isAnnouncementViewed(announcement.id, viewedIds)
  );
}

/**
 * Marks an announcement as viewed by adding it to the viewed list
 */
export function markAnnouncementAsViewed(
  announcementId: FeatureAnnouncementId,
  currentViewedIds: string[]
): string[] {
  if (isAnnouncementViewed(announcementId, currentViewedIds)) {
    return currentViewedIds;
  }

  return [...currentViewedIds, announcementId];
}

/**
 * Marks multiple announcements as viewed
 */
export function markAnnouncementsAsViewed(
  announcementIds: FeatureAnnouncementId[],
  currentViewedIds: string[]
): string[] {
  const newIds = [...currentViewedIds];
  let hasChanges = false;

  announcementIds.forEach((id) => {
    if (!newIds.includes(id)) {
      newIds.push(id);
      hasChanges = true;
    }
  });

  return hasChanges ? newIds : currentViewedIds;
}
